import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  FaSyncAlt,
  FaPlus,
  FaTrash,
  FaExclamationTriangle,
  FaCheckCircle,
  FaLock,
  FaChevronDown
} from 'react-icons/fa';
import './HeatmapSettingsTab.css';
import { getJourneyMining } from '../../../../services/customerAnalytics.service';
import { getDaysAgoIsoDate, getTodayIsoDate, toExclusiveToDate } from '../../shared/heatmapViewUtils';

const NAME_REGEX = /^[A-Za-z0-9][A-Za-z0-9 _-]*$/;
const KEY_REGEX = /^[a-z][a-z0-9_-]*$/;

const generateUuid = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * Derives a backend-compliant, extension-matchable captureKey:
 * 1. Prioritizes real DOM ID if present in candidate metadata/selector (sliced immediately to 50 chars).
 * 2. Falls back to lowercase name slug (sliced to 50 chars).
 * 3. Enforces ^[a-z][a-z0-9_-]*$ regex (max 64 chars).
 * 4. Auto-suffixes (_2, _3) to prevent duplicate key collisions.
 */
function deriveCaptureKey(candidate, existingActions = [], pageId, layoutVersion = 1) {
  let baseKey = '';

  // 1. Try extracting real DOM id from candidate metadata (sliced immediately to 50 chars)
  const idMatch = candidate?.captureKey?.match(/\|id=([^|]+)/i) ||
                  candidate?.captureKey?.match(/#([a-zA-Z0-9_-]+)/i);
  if (idMatch && idMatch[1]) {
    baseKey = idMatch[1].toLowerCase().replace(/[^a-z0-9_-]/g, '_').slice(0, 50);
  }

  // 2. Fallback to name slug (sliced to 50 chars)
  if (!baseKey) {
    baseKey = (candidate?.name || 'action')
      .toLowerCase()
      .replace(/^[^a-z]+/, '')
      .replace(/[^a-z0-9_-]/g, '_')
      .replace(/_+/g, '_')
      .slice(0, 50);
  }
  if (!baseKey || !/^[a-z]/.test(baseKey)) {
    baseKey = `action_${baseKey || 'step'}`.slice(0, 50);
  }

  // 3. Collision avoidance: auto-suffix if key already registered on this page
  let uniqueKey = baseKey;
  let counter = 1;
  const isDuplicate = (k) => existingActions.some(
    (a) => a.pageId === pageId && Number(a.layoutVersion || 1) === Number(layoutVersion) && a.captureKey === k
  );
  while (isDuplicate(uniqueKey)) {
    counter++;
    uniqueKey = `${baseKey.slice(0, 58)}_${counter}`;
  }

  return uniqueKey;
}

/**
 * Tab 1: Heatmap & Journey Capture Settings.
 * Manages capture toggles, registered pages, and registered journey action keys with ETag concurrency.
 * Follows strict append-only immutability rules and 9-field action schema.
 */
export default function HeatmapSettingsTab({
  settings,
  etag,
  isLoading = false,
  error = null,
  isConcurrencyConflict = false,
  onSave,
  onReload,
  onManageDomains,
  organizationId,
  siteCollectionId,
  fromDate,
  toDate,
  initialPagesCollapsed = true
}) {
  const [captureEnabled, setCaptureEnabled] = useState(false);
  const [advancedCaptureEnabled, setAdvancedCaptureEnabled] = useState(false);
  const [pages, setPages] = useState([]);
  const [actions, setActions] = useState([]);
  const [isPagesCollapsed, setIsPagesCollapsed] = useState(initialPagesCollapsed);

  // Track initially saved item IDs from backend to enforce append-only immutability
  const [savedActionIds, setSavedActionIds] = useState(() => new Set());

  // New Action Form Modal / State
  const [showActionModal, setShowActionModal] = useState(false);
  const [newActionName, setNewActionName] = useState('');
  const [newActionId, setNewActionId] = useState('');
  const [newActionKey, setNewActionKey] = useState('');
  const [newActionPageId, setNewActionPageId] = useState('');
  const [newActionStartsJourney, setNewActionStartsJourney] = useState(false);
  const [newActionEndsJourney, setNewActionEndsJourney] = useState(false);
  const [actionModalError, setActionModalError] = useState(null);

  // Auto-discovered candidate interactions
  const [candidates, setCandidates] = useState([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [selectedCandidateId, setSelectedCandidateId] = useState('');

  const [saveSuccess, setSaveSuccess] = useState(false);
  const [localSaving, setLocalSaving] = useState(false);

  // Initialize from props
  useEffect(() => {
    if (settings) {
      const basicEnabled = Boolean(settings.captureEnabled);
      setCaptureEnabled(basicEnabled);
      setAdvancedCaptureEnabled(Boolean(basicEnabled && settings.advancedCaptureEnabled));

      const initialPages = Array.isArray(settings.pages) ? [...settings.pages] : [];
      const initialActions = Array.isArray(settings.actions) ? [...settings.actions] : [];

      setPages(initialPages);
      setActions(initialActions);

      setSavedActionIds(new Set(initialActions.map((a) => a.actionId)));
    }
  }, [settings]);

  // Fetch candidate interactions for selected page
  useEffect(() => {
    if (!showActionModal || !newActionPageId || !organizationId || !siteCollectionId) {
      setCandidates([]);
      return;
    }

    let isMounted = true;
    setLoadingCandidates(true);

    const effectiveFrom = fromDate || getDaysAgoIsoDate(7);
    const effectiveTo = toDate ? toExclusiveToDate(toDate) : toExclusiveToDate(getTodayIsoDate());

    getJourneyMining(organizationId, siteCollectionId, {
      fromDate: effectiveFrom,
      toDate: effectiveTo,
      pageId: newActionPageId
    })
      .then((res) => {
        if (!isMounted) return;
        const pageCandidates = (res?.actions || []).filter(
          (a) => a.pageId === newActionPageId && a.name !== 'Interactive Step'
        );
        setCandidates(pageCandidates);
      })
      .catch(() => {
        if (isMounted) setCandidates([]);
      })
      .finally(() => {
        if (isMounted) setLoadingCandidates(false);
      });

    return () => {
      isMounted = false;
    };
  }, [showActionModal, newActionPageId, organizationId, siteCollectionId, fromDate, toDate]);

  // Centralized auto-save with normalized 9-field schema
  const persistConfiguration = async (updatedPages, updatedActions) => {
    setSaveSuccess(false);
    setLocalSaving(true);
    try {
      const normalizedActions = updatedActions.map((a) => {
        const matchingPage = updatedPages.find((p) => p.pageId === a.pageId) || updatedPages[0] || {};
        return {
          actionId: a.actionId || generateUuid(),
          pageId: a.pageId || matchingPage.pageId || '',
          layoutVersion: Number(a.layoutVersion != null ? a.layoutVersion : (matchingPage.layoutVersion || 1)),
          name: (a.name || '').trim(),
          captureKey: (a.captureKey || '').trim().toLowerCase(),
          startsJourney: Boolean(a.startsJourney),
          endsJourney: Boolean(a.endsJourney),
          flowId: a.flowId || null,
          stepId: a.stepId || null
        };
      });

      const payload = {
        captureEnabled,
        advancedCaptureEnabled: captureEnabled ? advancedCaptureEnabled : false,
        pages: updatedPages.map((p) => ({
          pageId: p.pageId,
          name: (p.name || '').trim(),
          layoutVersion: Number(p.layoutVersion || 1)
        })),
        actions: normalizedActions
      };

      if (typeof settings?.revision === 'number') {
        payload.revision = settings.revision;
      }

      await onSave(payload);
      setPages(updatedPages);
      setActions(normalizedActions);
      setSavedActionIds(new Set(normalizedActions.map((a) => a.actionId)));
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 4000);
      return true;
    } finally {
      setLocalSaving(false);
    }
  };

  // Action Modal handlers
  const handleOpenActionModal = () => {
    setNewActionName('');
    setNewActionId(generateUuid());
    setNewActionKey('');
    setNewActionPageId(pages[0]?.pageId || '');
    setNewActionStartsJourney(false);
    setNewActionEndsJourney(false);
    setSelectedCandidateId('');
    setActionModalError(null);
    setShowActionModal(true);
  };

  const handleAddAction = async (e) => {
    e.preventDefault();
    const trimmedName = newActionName.trim();
    const trimmedKey = newActionKey.trim().toLowerCase();

    if (!newActionPageId) {
      setActionModalError('Please select a registered page for this action.');
      return;
    }
    if (!trimmedName) {
      setActionModalError('Action name is required.');
      return;
    }
    if (trimmedName.length > 100 || !NAME_REGEX.test(trimmedName)) {
      setActionModalError(
        'Action name must start with a letter/digit and contain only letters, numbers, spaces, underscores, or hyphens (max 100 chars).'
      );
      return;
    }
    if (!trimmedKey || trimmedKey.length > 64 || !KEY_REGEX.test(trimmedKey)) {
      setActionModalError(
        'Capture key must start with a lowercase letter and contain only lowercase letters, digits, underscores, or hyphens (1-64 chars).'
      );
      return;
    }
    if (actions.length >= 100) {
      setActionModalError('Maximum of 100 actions allowed per site collection.');
      return;
    }

    const selectedPage = pages.find((p) => p.pageId === newActionPageId);
    const layoutVersion = Number(selectedPage?.layoutVersion) || 1;

    // Check duplicate (pageId, layoutVersion, captureKey)
    const duplicate = actions.some(
      (a) =>
        a.pageId === newActionPageId &&
        Number(a.layoutVersion || 1) === layoutVersion &&
        a.captureKey === trimmedKey
    );
    if (duplicate) {
      setActionModalError('An action with this capture key is already registered for this page and layout version.');
      return;
    }

    const newActionItem = {
      actionId: newActionId.trim() || generateUuid(),
      pageId: newActionPageId,
      layoutVersion,
      name: trimmedName,
      captureKey: trimmedKey,
      startsJourney: Boolean(newActionStartsJourney),
      endsJourney: Boolean(newActionEndsJourney),
      flowId: null,
      stepId: null
    };

    const nextActions = [...actions, newActionItem];

    try {
      await persistConfiguration(pages, nextActions);
      setShowActionModal(false);
      setActionModalError(null);
    } catch (err) {
      setActionModalError(err?.message || 'Failed to save action to server.');
    }
  };

  // Only allow deleting newly added, unsaved actions in local draft
  const handleDeleteAction = (actionId) => {
    if (savedActionIds.has(actionId)) return;
    setActions(actions.filter((a) => a.actionId !== actionId));
  };

  return (
    <div className="heatmap-settings-tab" data-testid="heatmap-settings-tab">
      <div className="settings-tab-header">
        <div>
          <h3>Heatmap & Behavioral Capture Settings</h3>
          <p className="settings-desc">
            Define tracked application pages and conversion actions.
          </p>
        </div>
        <div className="settings-header-actions">
          {etag && <span className="etag-badge" title="Optimistic Concurrency ETag">ETag: {etag}</span>}
          <button
            type="button"
            className="settings-reload-btn"
            onClick={onReload}
            disabled={isLoading || localSaving}
            title="Reload settings from server"
          >
            <FaSyncAlt className={isLoading ? 'spinning' : ''} /> Reload
          </button>
        </div>
      </div>

      {/* Conflict / Error Banner */}
      {isConcurrencyConflict && (
        <div className="settings-alert-banner conflict" role="alert" data-testid="settings-conflict-alert">
          <FaExclamationTriangle className="alert-icon" />
          <div className="alert-content">
            <strong>Optimistic Concurrency Conflict</strong>
            <p>Settings were updated by another session. Please reload before saving.</p>
          </div>
          <button type="button" className="alert-action-btn" onClick={onReload}>Reload Now</button>
        </div>
      )}

      {error && !isConcurrencyConflict && (
        <div className="settings-alert-banner error" role="alert">
          <FaExclamationTriangle className="alert-icon" />
          <div className="alert-content">
            <strong>Error</strong>
            <p>{error}</p>
          </div>
        </div>
      )}

      {saveSuccess && (
        <div className="settings-alert-banner success" role="alert" data-testid="settings-save-success">
          <FaCheckCircle className="alert-icon" />
          <div className="alert-content">
            <strong>Saved Successfully</strong>
            <p>Heatmap configuration updated. New ETag recorded.</p>
          </div>
        </div>
      )}

      {/* Registered Journey Actions Table */}
      <div className="settings-card">
        <div className="card-header-row">
          <div>
            <h4 className="card-section-title">
              Registered Journey Actions ({actions.length}/100)
            </h4>
            <p className="card-section-desc">
              Key buttons and interactions used in conversion funnels and ML journey mining.
            </p>
          </div>
          <button
            type="button"
            className="btn-add-item"
            data-testid="btn-open-add-action"
            onClick={handleOpenActionModal}
            disabled={actions.length >= 100 || pages.length === 0}
            title={pages.length === 0 ? 'Register a page first' : 'Add action'}
          >
            <FaPlus /> Add Action
          </button>
        </div>

        {actions.length === 0 ? (
          <div className="empty-table-state">No journey actions registered. Click &quot;Add Action&quot; to register one.</div>
        ) : (
          <table className="settings-table">
            <thead>
              <tr>
                <th>Action Name</th>
                <th>Page</th>
                <th>Capture Key</th>
                <th>Journey Role</th>
                <th>Action ID</th>
                <th style={{ width: '100px', textAlign: 'center' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {actions.map((a) => {
                const isPersisted = savedActionIds.has(a.actionId);
                const pageMatch = pages.find((p) => p.pageId === a.pageId);
                return (
                  <tr key={a.actionId}>
                    <td className="cell-primary">{a.name}</td>
                    <td>{pageMatch ? `${pageMatch.name} (v${a.layoutVersion || pageMatch.layoutVersion || 1})` : a.pageId || '—'}</td>
                    <td className="cell-badge"><span className="key-chip">{a.captureKey}</span></td>
                    <td>
                      {a.startsJourney && <span className="badge-flag start">Starts</span>}
                      {a.endsJourney && <span className="badge-flag end">Ends</span>}
                      {!a.startsJourney && !a.endsJourney && <span style={{ color: '#94a3b8' }}>Step</span>}
                    </td>
                    <td className="cell-code"><code>{a.actionId}</code></td>
                    <td style={{ textAlign: 'center' }}>
                      {isPersisted ? (
                        <span className="badge-persisted" title="Registered on server (Immutable)">
                          <FaLock /> Saved
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="btn-delete-row"
                          onClick={() => handleDeleteAction(a.actionId)}
                          title="Delete draft action"
                        >
                          <FaTrash />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Registered Pages Table (Collapsible at bottom) */}
      <div className="settings-card pages-collapsible-card">
        <div
          className="card-header-row card-header-collapsible"
          style={{ marginBottom: isPagesCollapsed ? 0 : 16 }}
        >
          <button
            type="button"
            className="btn-collapse-toggle"
            data-testid="btn-toggle-pages-collapse"
            onClick={() => setIsPagesCollapsed((prev) => !prev)}
            aria-expanded={!isPagesCollapsed}
            title={isPagesCollapsed ? 'Expand application pages' : 'Collapse application pages'}
          >
            <FaChevronDown className={`collapse-chevron ${isPagesCollapsed ? 'collapsed' : ''}`} />
            <div>
              <h4 className="card-section-title">
                Registered Application Pages ({pages.length}/50)
              </h4>
              <p className="card-section-desc">
                Pages tracked by the coordinate density engine for heatmap overlays. Registered pages are immutable.
              </p>
            </div>
          </button>
        </div>

        {!isPagesCollapsed && (
          pages.length === 0 ? (
            <div className="empty-table-state">No pages registered.</div>
          ) : (
            <table className="settings-table">
              <thead>
                <tr>
                  <th>Page Name</th>
                  <th>Page ID</th>
                  <th>Layout Version</th>
                </tr>
              </thead>
              <tbody>
                {pages.map((p) => (
                  <tr key={p.pageId}>
                    <td className="cell-primary">{p.name}</td>
                    <td className="cell-code"><code>{p.pageId}</code></td>
                    <td>v{p.layoutVersion || 1}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}
      </div>


      {/* Modal: Add Action */}
      {showActionModal && (
        <div className="settings-modal-overlay">
          <div className="settings-modal-card">
            <h4>Register Journey Action</h4>
            {actionModalError && <div className="modal-error-banner">{actionModalError}</div>}
            <form onSubmit={handleAddAction}>
              <div className="form-group">
                <label>Associated Page</label>
                <select
                  className="modal-select"
                  value={newActionPageId}
                  onChange={(e) => {
                    setNewActionPageId(e.target.value);
                    setSelectedCandidateId('');
                    setNewActionName('');
                    setNewActionKey('');
                  }}
                  required
                >
                  <option value="">Select a registered page...</option>
                  {pages.map((p) => (
                    <option key={p.pageId} value={p.pageId}>
                      {p.name} (v{p.layoutVersion || 1})
                    </option>
                  ))}
                </select>
              </div>

              {/* Detected Page Interaction Dropdown */}
              <div className="form-group">
                <label>
                  Detected Page Interaction {loadingCandidates && <span className="spinner-inline">(Loading...)</span>}
                </label>
                <select
                  className="modal-select"
                  data-testid="select-detected-candidate"
                  value={selectedCandidateId}
                  onChange={(e) => {
                    const candId = e.target.value;
                    setSelectedCandidateId(candId);
                    const found = candidates.find((c) => c.actionId === candId);
                    if (found) {
                      setNewActionName(found.name);
                      const selectedPage = pages.find((p) => p.pageId === newActionPageId);
                      const layoutVer = selectedPage?.layoutVersion || 1;
                      setNewActionKey(deriveCaptureKey(found, actions, newActionPageId, layoutVer));
                    }
                  }}
                >
                  <option value="">-- Select detected interaction (or enter manually below) --</option>
                  {candidates.map((c) => (
                    <option key={c.actionId} value={c.actionId}>
                      {c.name} {c.screen ? `(${c.screen})` : ''}
                    </option>
                  ))}
                </select>
                <span className="field-hint">
                  {candidates.length > 0
                    ? `${candidates.length} interaction(s) observed from traffic on this page.`
                    : 'No observed clicks on this page yet. Enter custom details below.'}
                </span>
              </div>

              <div className="form-group">
                <label>Action Name</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Submit Order Click"
                  value={newActionName}
                  onChange={(e) => setNewActionName(e.target.value)}
                  className="modal-input"
                />
              </div>

              <div className="form-group">
                <label>Capture Key (Lowercase, e.g. btn_submit_order)</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. btn_submit_order"
                  value={newActionKey}
                  onChange={(e) => setNewActionKey(e.target.value.toLowerCase())}
                  className="modal-input"
                />
                <span className="field-hint">
                  Matches button attribute: <code>id=&quot;{newActionKey || '...'}&quot;</code> or <code>data-iap-key=&quot;{newActionKey || '...'}&quot;</code>.
                </span>
              </div>

              <div className="form-group">
                <label>Journey Boundary Role</label>
                <div className="modal-checkbox-group">
                  <label className="modal-checkbox-label">
                    <input
                      type="checkbox"
                      checked={newActionStartsJourney}
                      onChange={(e) => setNewActionStartsJourney(e.target.checked)}
                    />
                    Starts Journey
                  </label>
                  <label className="modal-checkbox-label">
                    <input
                      type="checkbox"
                      checked={newActionEndsJourney}
                      onChange={(e) => setNewActionEndsJourney(e.target.checked)}
                    />
                    Ends Journey
                  </label>
                </div>
              </div>

              {/* NOTE: Action ID (UUID) is hidden from UI; auto-generated in background */}

              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowActionModal(false)} disabled={localSaving}>
                  Cancel
                </button>
                <button type="submit" className="btn-submit" data-testid="btn-submit-add-action" disabled={localSaving}>
                  {localSaving ? 'Saving...' : 'Add Action'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

HeatmapSettingsTab.propTypes = {
  settings: PropTypes.object,
  etag: PropTypes.string,
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  isConcurrencyConflict: PropTypes.bool,
  onSave: PropTypes.func.isRequired,
  onReload: PropTypes.func.isRequired,
  onManageDomains: PropTypes.func,
  organizationId: PropTypes.string,
  siteCollectionId: PropTypes.string,
  fromDate: PropTypes.string,
  toDate: PropTypes.string,
  initialPagesCollapsed: PropTypes.bool
};
